import {
  WORKSHEET_IMPORT_MAX_BYTES,
  WORKSHEET_IMPORT_REFUSAL_CODE_VALUES,
} from '@cc/schema';
import {
  LANGUAGES,
  SECTION_FIELDS,
  type WorksheetCategory,
  type WorksheetDraft,
  type WorksheetLanguage,
} from './model';

/**
 * E34 · worksheet IMPORT on the portal (block 5b; cc-homework, chapter "Worksheets — Templates & library
 * (portal, E34)") — what the Builder's import section, the imported draft and the packet list share: the
 * mutation's fields, the result's types, and PURE helpers (the upload's plan, a draft as the Builder's draft,
 * the banner's source, the intake packet's steps). No i18n here: helpers return KEYS or data, the screens
 * translate. Nothing is saved by an import (Q-e34-15 A): Save is DefineWorksheetTemplate with `importedFrom`,
 * "Make an intake packet" is DefinePathway.
 */

export const IMPORT_MAX_BYTES = WORKSHEET_IMPORT_MAX_BYTES;
export const IMPORT_REFUSAL_CODES = WORKSHEET_IMPORT_REFUSAL_CODE_VALUES;

/** Photos are shrunk in the browser to about this many pixels on the long side (Q-e34-15 A). */
export const PHOTO_LONG_SIDE = 2000;

/** The progress estimate: about 10 s a page (about a minute for a 14-page packet). */
export const SECONDS_PER_PAGE = 10;

export const IMPORT_RESULT_FIELDS = [
  'importId',
  'practiceId',
  { importedFrom: ['format', 'pages'] },
  { documents: ['templates', 'knownInstruments', 'skipped'] },
  {
    drafts: [
      'kind',
      'pages',
      {
        draft: [
          'name',
          'category',
          'language',
          { sections: SECTION_FIELDS },
          { scoring: ['kind', 'fields', { bands: ['min', 'max', 'label'] }] },
          'signatureRequired',
          'repeatable',
        ],
      },
      { notes: ['code', 'pages', 'source', 'ref', 'count'] },
      'instrumentId',
      'instrument',
      'inCatalogue',
      'reason',
    ],
  },
  'instrumentIds',
  'code',
];

export type ImportedFrom = { format: 'pdf' | 'docx' | 'image'; pages: number | null };

export type ImportNote = {
  code: string;
  pages?: number[] | null;
  source?: string | null;
  ref?: string | null;
  count?: number | null;
};

export type ImportTemplateDraft = {
  name: string;
  category: WorksheetCategory;
  language: string;
  sections: Array<{
    sectionId: string;
    title?: string | null;
    text?: string | null;
    video?: { source: 'learn' | 'youtube' | 'vimeo'; ref: string; title: string; duration?: number | null } | null;
    fields: Array<{
      fieldId: string;
      type: WorksheetDraft['sections'][number]['fields'][number]['type'];
      label: string;
      required?: boolean | null;
      options?: string[] | null;
      min?: number | null;
      max?: number | null;
    }>;
  }>;
  scoring?: { kind: string; fields: string[]; bands: Array<{ min: number; max: number; label: string }> } | null;
  signatureRequired: boolean;
  repeatable: boolean;
};

export type ImportItem = {
  kind: 'template' | 'knownInstrument' | 'skip';
  pages: number[];
  draft?: ImportTemplateDraft | null;
  notes: ImportNote[];
  instrumentId?: string | null;
  instrument?: string | null;
  inCatalogue?: boolean | null;
  reason?: string | null;
};

export type ImportResult = {
  importId: string;
  practiceId: string | null;
  importedFrom: ImportedFrom | null;
  documents: { templates: number; knownInstruments: number; skipped: number } | null;
  drafts: ImportItem[];
  instrumentIds: string[];
  code: string | null;
};

// ── the upload's plan ───────────────────────────────────────────────────────────────────────────────

export type PickedFile = { name: string; type: string; size: number };

export type UploadKind = 'pdf' | 'docx' | 'image' | 'unsupported';

/** What a picked file is, by its type or its name (the API reads the bytes again — this only guides the picker). */
export const uploadKindOf = (f: Pick<PickedFile, 'name' | 'type'>): UploadKind => {
  const name = f.name.toLowerCase();
  const type = (f.type || '').toLowerCase();
  if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (type.includes('wordprocessingml') || name.endsWith('.docx')) return 'docx';
  if (/^image\/(jpeg|png|webp|heic|heif)$/.test(type) || /\.(jpe?g|png|webp|heic|heif)$/.test(name)) {
    return 'image';
  }
  return 'unsupported';
};

/**
 * Adding files to the upload: ONE PDF or Word file, or photos (appended, in the order chosen). A document
 * replaces what was there; a photo after a document replaces the document too — mixing is not one form. The
 * problem is a KEY the screen shows under the drop zone.
 */
export const addToUpload = <F extends Pick<PickedFile, 'name' | 'type'>>(
  current: readonly F[],
  added: readonly F[],
): { files: F[]; problem: string | null } => {
  const kinds = added.map(uploadKindOf);
  if (kinds.some((k) => k === 'unsupported')) {
    return { files: [...current], problem: 'worksheets.import.notSupported' };
  }
  const documents = added.filter((_, i) => kinds[i] !== 'image');
  const photos = added.filter((_, i) => kinds[i] === 'image');
  if (documents.length > 1 || (documents.length === 1 && photos.length > 0)) {
    return { files: [...current], problem: 'worksheets.import.oneDocument' };
  }
  if (documents.length === 1) return { files: [documents[0]], problem: null };
  const keep = current.filter((f) => uploadKindOf(f) === 'image');
  return { files: [...keep, ...photos], problem: null };
};

/** Whether the bytes go at all: over the limit, only the names and sizes go — the API records the refusal. */
export const sendsBytes = (totalBytes: number): boolean => totalBytes <= IMPORT_MAX_BYTES;

/** A PDF's pages, roughly, for the progress estimate only (the API counts them properly). */
export const estimatePdfPages = (text: string): number => {
  const n = (text.match(/\/Type\s*\/Page(?![a-z])/g) ?? []).length;
  return Math.max(1, n);
};

export const estimateSeconds = (pages: number): number =>
  Math.max(SECONDS_PER_PAGE, Math.round(pages) * SECONDS_PER_PAGE);

// ── the result ──────────────────────────────────────────────────────────────────────────────────────

/** One document opens straight in the editor; anything more is a packet (the document list). */
export const isSingleDraft = (r: Pick<ImportResult, 'drafts'>): boolean =>
  r.drafts.length === 1 && r.drafts[0].kind === 'template' && !!r.drafts[0].draft;

/** The banner's "Imported from …": a KEY and its count. */
export const sourceOf = (
  from: ImportedFrom | null,
): { key: string; count?: number } => {
  if (!from) return { key: 'worksheets.import.draft.source.pdf' };
  if (from.format === 'docx') return { key: 'worksheets.import.draft.source.docx' };
  if (from.format === 'image') {
    return { key: 'worksheets.import.draft.source.image', count: from.pages ?? 1 };
  }
  return from.pages
    ? { key: 'worksheets.import.draft.source.pdfPages', count: from.pages }
    : { key: 'worksheets.import.draft.source.pdf' };
};

/** Pages as the list shows them: "1–4", "8", "10, 12". */
export const pagesLabel = (pages: readonly number[] | null | undefined): string => {
  const ps = [...(pages ?? [])].sort((a, b) => a - b);
  if (ps.length === 0) return '';
  const consecutive = ps.every((p, i) => i === 0 || p === ps[i - 1] + 1);
  if (consecutive && ps.length > 1) return `${ps[0]}–${ps[ps.length - 1]}`;
  return ps.join(', ');
};

/** An imported template as the Builder's draft — the same editor a new form opens in. */
export const draftFromImport = (d: ImportTemplateDraft): WorksheetDraft => ({
  name: d.name ?? '',
  category: d.category,
  language: (LANGUAGES as readonly string[]).includes(d.language)
    ? (d.language as WorksheetLanguage)
    : 'en',
  sections: (d.sections ?? []).map((s) => ({
    sectionId: s.sectionId,
    title: s.title ?? '',
    text: s.text ?? '',
    video: s.video
      ? { source: s.video.source, ref: s.video.ref, title: s.video.title, duration: s.video.duration ?? null }
      : null,
    fields: (s.fields ?? []).map((f) => ({
      fieldId: f.fieldId,
      type: f.type,
      label: f.label,
      required: !!f.required,
      options: f.options ?? [],
      min: f.min ?? null,
      max: f.max ?? null,
    })),
  })),
  scoring: d.scoring
    ? { enabled: true, fields: d.scoring.fields ?? [], bands: d.scoring.bands ?? [] }
    : { enabled: false, fields: [], bands: [] },
  signatureRequired: !!d.signatureRequired,
  repeatable: !!d.repeatable,
  copiedFrom: null, // an import is not a copy
});

export const questionCount = (d: ImportTemplateDraft | null | undefined): number =>
  (d?.sections ?? []).reduce((n, s) => n + (s.fields?.length ?? 0), 0);

// ── Make an intake packet (DefinePathway) ───────────────────────────────────────────────────────────

/** The days a packet step is due after it is released (the board's example: 3). */
export const PACKET_DUE_DAYS = 3;

/**
 * The intake packet's one section, derived from Save all: every saved template a worksheet step, then every
 * questionnaire found in the catalogue an assessment step — all released immediately (the existing
 * intake-packet shape). A questionnaire not in the catalogue is no step.
 */
export const intakePacketSections = (
  saved: ReadonlyArray<{ templateId: string; name: string }>,
  instruments: ReadonlyArray<{ instrumentId: string; name: string }>,
  sectionTitle: string,
) => {
  let n = 0;
  const step = (kind: 'worksheet' | 'assessment', ref: string, title: string) => {
    n += 1;
    return {
      stepId: `i${n}`,
      kind,
      target: { type: kind, ref },
      title,
      instructions: null,
      release: { rule: 'immediately' },
      dueDays: PACKET_DUE_DAYS,
    };
  };
  return [
    {
      section: sectionTitle,
      steps: [
        ...saved.map((s) => step('worksheet', s.templateId, s.name)),
        ...instruments.map((i) => step('assessment', i.instrumentId, i.name)),
      ],
    },
  ];
};
